<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter, type LocationQuery, type LocationQueryValue } from "vue-router";
import { UiText, UiIcon, UiInfoBox, UiTabs, UiTabsItem, UiButton, UiTooltip } from "@shopexperts/shared";
import type { AdminWalletTransaction, AdminWalletTransactionsQueryParams } from "@shopexperts/legacy-api";
import {
  useAdminWalletTransactions,
  useReconcileAllAdminWalletAllocations,
} from "@/app/composables/wallet";
import AdminWalletTransactionFilters from "@/app/components/expert-manager/wallet/AdminWalletTransactionFilters.vue";
import AdminWalletTransactionCard from "@/app/components/expert-manager/wallet/AdminWalletTransactionCard.vue";
import AdminPayoutRequestsTab from "@/app/components/expert-manager/payouts/AdminPayoutRequestsTab.vue";
import AdminWalletStatisticsTab from "@/app/components/expert-manager/wallet/statistics/AdminWalletStatisticsTab.vue";
import ReleaseHoldDialog from "@/app/components/expert-manager/wallet/ReleaseHoldDialog.vue";
import DeleteWalletTransactionDialog from "@/app/components/expert-manager/wallet/DeleteWalletTransactionDialog.vue";
import {
  canDeleteAdminWalletTransaction,
} from "@/app/components/expert-manager/wallet/useAdminWalletTransaction";

const route = useRoute();
const router = useRouter();

// Filter persistence: the active filters live in the URL query, so navigating
// away and pressing back restores the exact same filtered view.
const NUMERIC_FILTER_KEYS = [
  "amount_min_cents",
  "amount_max_cents",
  "organization_id",
  "quote_id",
  "project_id",
  "related_transaction_id",
  "expert_level_id",
  "quote_payment_event_id",
] as const satisfies readonly (keyof AdminWalletTransactionsQueryParams)[];

const STRING_FILTER_KEYS = [
  "search",
  "transaction_kind",
  "direction",
  "currency",
  "created_from",
  "created_to",
  "payment_provider",
  "provider_payment_intent_id",
  "provider_payment_link_id",
  "idempotency_key",
] as const satisfies readonly (keyof AdminWalletTransactionsQueryParams)[];

function firstQueryValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw == null ? undefined : raw;
}

function queryToFilters(query: LocationQuery): AdminWalletTransactionsQueryParams {
  const parsed: Record<string, unknown> = {};
  for (const key of STRING_FILTER_KEYS) {
    const value = firstQueryValue(query[key]);
    if (value !== undefined && value !== "") parsed[key] = value;
  }
  for (const key of NUMERIC_FILTER_KEYS) {
    const value = firstQueryValue(query[key]);
    if (value === undefined || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num)) parsed[key] = num;
  }
  if (firstQueryValue(query.eligible_for_hold_release) === "true") {
    parsed.eligible_for_hold_release = true;
  }
  return parsed as AdminWalletTransactionsQueryParams;
}

function filtersToQuery(value: AdminWalletTransactionsQueryParams): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (raw === undefined || raw === null || raw === "") continue;
    query[key] = String(raw);
  }
  return query;
}

const filters = ref<AdminWalletTransactionsQueryParams>(queryToFilters(route.query));

type TabValue = "transactions" | "payouts" | "statistics";

function parseTab(value: unknown): TabValue {
  return value === "payouts" || value === "statistics" ? value : "transactions";
}

const activeTab = ref<TabValue>(parseTab(route.query.tab));

watch(activeTab, (next) => {
  const current = typeof route.query.tab === "string" ? route.query.tab : "transactions";
  if (current === next) return;
  const nextQuery = { ...route.query, tab: next } as Record<string, string>;
  if (next === "transactions") delete nextQuery.tab;
  router.replace({ query: nextQuery });
});

// Mirror filter changes back into the URL (replace, so we don't spam history).
watch(
  filters,
  (next) => {
    const query = filtersToQuery(next);
    if (activeTab.value !== "transactions") query.tab = activeTab.value;
    if (JSON.stringify(query) === JSON.stringify(route.query)) return;
    router.replace({ query });
  },
  { deep: true },
);

// In-page navigation (e.g. a payout card's transaction link) changes the query
// without remounting the page, so sync it back into the tab and filter state.
watch(
  () => route.query,
  (query) => {
    const nextTab = parseTab(query.tab);
    if (nextTab !== activeTab.value) activeTab.value = nextTab;
    const parsed = queryToFilters(query);
    if (JSON.stringify(parsed) !== JSON.stringify(filters.value)) filters.value = parsed;
  },
);

const { transactions, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
  useAdminWalletTransactions({ filters });

const { mutate: reconcileAllAllocations, isPending: isReconcilingAll } =
  useReconcileAllAdminWalletAllocations();

const hasActiveFilters = computed(() => Object.keys(filters.value).length > 0);
const nowMs = ref(Date.now());
let deleteWindowTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  deleteWindowTimer = setInterval(() => {
    nowMs.value = Date.now();
  }, 30_000);
});

onUnmounted(() => {
  if (deleteWindowTimer) clearInterval(deleteWindowTimer);
});

const latestTransactionIdByWallet = computed(() => {
  const latestByWallet = new Map<number, number>();
  for (const transaction of transactions.value) {
    const current = latestByWallet.get(transaction.wallet_id);
    if (current === undefined || transaction.id > current) {
      latestByWallet.set(transaction.wallet_id, transaction.id);
    }
  }
  return latestByWallet;
});

function canDeleteTransaction(transaction: AdminWalletTransaction) {
  return canDeleteAdminWalletTransaction(transaction, {
    hasActiveFilters: hasActiveFilters.value,
    latestLoadedTransactionId: latestTransactionIdByWallet.value.get(transaction.wallet_id),
    nowMs: nowMs.value,
  });
}

const releaseTarget = ref<AdminWalletTransaction | null>(null);
const releaseOpen = ref(false);
const deleteTarget = ref<AdminWalletTransaction | null>(null);
const deleteOpen = ref(false);

function onReleaseHold(transaction: AdminWalletTransaction) {
  releaseTarget.value = transaction;
  releaseOpen.value = true;
}

function onDeleteTransaction(transaction: AdminWalletTransaction) {
  deleteTarget.value = transaction;
  deleteOpen.value = true;
}

// Statistics drill-down: land on the transactions tab scoped to one organization.
// A single navigation (instead of mutating tab + filters directly) lets the
// route.query watcher hydrate both without the two URL-sync watchers racing;
// push keeps the statistics tab one Back-press away.
function showTransactionsForOrganization(organizationId: number) {
  router.push({ query: { organization_id: String(organizationId) } });
}

// Infinite scroll observer
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (el) => {
  if (observer) observer.disconnect();
  if (!el) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: "200px" },
  );
  observer.observe(el);
});
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-secondary p-8 font-light">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <!-- Header -->
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h1 class="text-3xl font-normal">Wallet Transactions</h1>
          <UiText
            as="p"
            variant="p"
            class="max-w-2xl text-se-gray-600"
            text="Every wallet ledger entry across all organizations. Filter by type, direction, dates, amounts, references and more, and release eligible holds."
          />
        </div>
        <UiTooltip
          content="Rebuild debit-to-source funding links for every wallet, retroactively covering history written before allocation tracking existed. Idempotent — existing links are kept and only gaps are filled."
        >
          <UiButton
            variant="outline"
            size="sm"
            label="Reconcile all wallets"
            icon="refresh-cw"
            :loading="isReconcilingAll"
            @click="reconcileAllAllocations()"
          />
        </UiTooltip>
      </div>

      <UiTabs v-model="activeTab" default-value="transactions">
        <UiTabsItem value="transactions" label="Transactions">
          <div class="flex flex-col gap-6 pt-4">
            <AdminWalletTransactionFilters v-model="filters" show-organization />

            <!-- Error -->
            <UiInfoBox v-if="error" variant="error" title="Couldn't load transactions">
              <UiText as="span" variant="p-small">
                <span>Something went wrong loading the transactions. Refresh to try again.</span>
              </UiText>
            </UiInfoBox>

            <!-- Loading -->
            <div v-else-if="isLoading" class="flex flex-col gap-3">
              <div v-for="n in 5" :key="n" class="h-28 animate-pulse rounded-xl bg-se-gray-100" />
            </div>

            <!-- Empty -->
            <div
              v-else-if="transactions.length === 0"
              class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-se-gray-300 py-16 text-center"
            >
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-se-gray-100">
                <UiIcon name="wallet" class="h-6 w-6 text-se-gray-500" />
              </div>
              <UiText
                as="h4"
                variant="h6"
                class="font-semibold"
                :text="hasActiveFilters ? 'No transactions match your filters' : 'No transactions yet'"
              />
              <UiText
                as="p"
                variant="p-small"
                class="text-se-gray-600"
                :text="hasActiveFilters
                  ? 'Try adjusting or clearing the filters above.'
                  : 'Wallet activity across organizations will appear here.'"
              />
            </div>

            <!-- List -->
            <template v-else>
              <ul class="flex flex-col">
                <AdminWalletTransactionCard
                  v-for="(transaction, index) in transactions"
                  :key="transaction.id"
                  :transaction="transaction"
                  show-organization
                  :is-last="index === transactions.length - 1"
                  :can-delete-transaction="canDeleteTransaction(transaction)"
                  @release-hold="onReleaseHold"
                  @delete-transaction="onDeleteTransaction"
                />
              </ul>

              <div ref="sentinel" class="h-1" />

              <div v-if="isFetchingNextPage" class="py-3 text-center">
                <UiText as="p" variant="p-small" class="text-se-gray-500" text="Loading more…" />
              </div>
            </template>
          </div>
        </UiTabsItem>
        <UiTabsItem value="payouts" label="Payout Requests">
          <AdminPayoutRequestsTab show-organization class="pt-4" />
        </UiTabsItem>
        <UiTabsItem value="statistics" label="Statistics">
          <AdminWalletStatisticsTab
            :active="activeTab === 'statistics'"
            @view-transactions="showTransactionsForOrganization"
          />
        </UiTabsItem>
      </UiTabs>
    </div>

    <ReleaseHoldDialog v-model="releaseOpen" :transaction="releaseTarget" />
    <DeleteWalletTransactionDialog v-model="deleteOpen" :transaction="deleteTarget" />
  </main>
</template>
